import ReactDOM from "react-dom/client";
import { StrictMode } from "react";
import {
    backButton,
    settingsButton,
    viewport,
    themeParams,
    miniApp,
    initData,
    $debug,
    init as initSDK,
    closingBehavior,
    swipeBehavior,
    mainButton,
} from "@telegram-apps/sdk-react";
import "@telegram-apps/telegram-ui/dist/styles.css";
import "antd-mobile/es/global";
import * as Sentry from "@sentry/react";

import { isTest, isPro, VITE_BUILD_ENV } from "./config/index.ts";
import { App } from "./components/App.tsx";
import { EnvUnsupported } from "./components/EnvUnsupported";

import "~/plugin/amfe-flexible";
import "./polyfill";
import "./i18n/index";
import "./index.scss";
// Mock the environment in case, we are outside Telegram.
import "./mockEnv.ts";
import { setupFirebase } from "./hook/useFirebase.ts";

const root = ReactDOM.createRoot(document.getElementById("root")!);

try {
    // Configure all application dependencies.
    init(isTest);
    setupSentry();
    setupFirebase();

    root.render(
        <StrictMode>
            <Sentry.ErrorBoundary fallback={EnvUnsupported}>
                <App />
            </Sentry.ErrorBoundary>
        </StrictMode>
    );
} catch (e) {
    console.error(e);
    root.render(<EnvUnsupported />);
}

function init(debug: boolean): void {
    // Set @telegram-apps/sdk-react debug mode.
    $debug.set(debug);

    // Initialize special event handlers for Telegram Desktop, Android, iOS, etc.
    // Also, configure the package.
    initSDK();

    // Add Eruda if needed.
    if (debug) {
        import("eruda").then((lib) => lib.default.init()).catch(console.error);
    }

    // Check if all required components are supported.
    if (!backButton.isSupported() || !miniApp.isSupported()) {
        throw new Error("ERR_NOT_SUPPORTED");
    }

    // Mount all components used in the project.
    miniApp.mount();
    themeParams.mount();
    mainButton.mount();
    backButton.mount();
    settingsButton.mount();
    closingBehavior.mount();
    if (swipeBehavior.isSupported()) {
        swipeBehavior.mount();
    }
    initData.restore();

    void viewport
        .mount()
        .catch((e) => {
            console.error("Something went wrong mounting the viewport", e);
        })
        .then(() => {
            viewport.bindCssVars();
        });

    // Define components-related CSS variables.
    miniApp.bindCssVars();
    themeParams.bindCssVars();
}

function setupSentry() {
    Sentry.init({
        enabled: isPro,
        debug: !isPro,
        dsn: "https://b6c0207a848e737136b53c782dc7649b@o4508475510358016.ingest.us.sentry.io/4508476975677440",
        sampleRate: 1,
        environment: VITE_BUILD_ENV,
        integrations: [],
        enableTracing: false,
    });
}
